<template>
  <v-sheet class="io-filter-bar">
    <!-- Loading overlay for unmarking event -->
    <v-overlay
      :value="unmarkingEvent"
      absolute
      opacity="0.8"
      z-index="9999"
    >
      <v-card class="pa-4" dark>
        <div class="d-flex align-center">
          <v-progress-circular
            indeterminate
            color="white"
            size="24"
            width="2"
            class="mr-3"
          ></v-progress-circular>
          <span>Merkitään eventtiä vapaaksi...</span>
        </div>
      </v-card>
    </v-overlay>

    <v-tabs
      v-if="!$vuetify.breakpoint.mobile"
      v-model="activeTab"
      style="max-height: 48px;"
      small
      centered
      :show-arrows="false"
    >
      <v-tab>
        <v-icon left>mdi-format-list-bulleted</v-icon>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-filter-outline</v-icon>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-chart-donut</v-icon>
      </v-tab>
    </v-tabs>
    <div class="d-flex justify-space-around" v-else>
      <v-btn small @click="activeTab = 0" text :color="activeTab === 0 ? 'primary' : ''">
        <v-icon size="20">mdi-format-list-bulleted</v-icon>
      </v-btn>
      <v-btn small @click="activeTab = 1" text :color="activeTab === 1 ? 'primary' : ''">
        <v-icon size="20">mdi-filter-outline</v-icon>
      </v-btn>
      <v-btn small @click="activeTab = 2" text :color="activeTab === 2 ? 'primary' : ''">
        <v-icon size="20">mdi-chart-donut</v-icon>
      </v-btn>
      <v-btn small @click="activeTab = 3" text :color="activeTab === 3 ? 'primary' : ''">
        <v-icon size="20">mdi-map</v-icon>
      </v-btn>
    </div>

    <v-tabs-items v-model="activeTab" :style="`height: calc(100% - ${$vuetify.breakpoint.mobile ? 28 : 48}px); overflow-y: scroll;`">
      <v-tab-item>
        <FilterBarContent
          :h="h"
          :events="events"
          :playingEvent="playingEvent"
          :leagueFilter="leagueFilter"
        />
      </v-tab-item>
      <v-tab-item>
        <CreateFilter
          :unscrollable="true"
          @update="updateFilter"
          :initFilter="filter"
          :inline="true"
        />
      </v-tab-item>
      <v-tab-item>
        <FilterData
          :h="h"
          :events="events"
        ></FilterData>
      </v-tab-item>
      <v-tab-item>
        <div style="height: 100%;" class="mt-2">
          <FilterMap :unclosable="true" v-if="showFilterMap" />
        </div>
      </v-tab-item>
    </v-tabs-items>
  </v-sheet>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import FilterBarContent from './FilterBarContent.vue'
import CreateFilter from './CreateFilter.vue'
import FilterData from './FilterData.vue'
import FilterMap from './FilterMap.vue'
import ax from '@/utils/axios/axios'
import { getSeasonYear, newRulesApply } from '@/utils/filterItems.js'

export default {
  components: { FilterBarContent, CreateFilter, FilterData, FilterMap },
  props: ['events', 'playingEvent', 'leagueFilter', 'h'],
  data: () => ({
    activeTab: 0,
    unmarkingEvent: false
  }),
  created() {
    // Seasons are needed to resolve whether the nyp map should be shown
    if(this.currentLeagueName === 'nyp' && (!this.seasons || !this.seasons.length)) {
      this.initSeasons(this.leagueId)
    }
  },
  shortcuts: {
    shift_e: function() {
      if(this.user.tiimio_admin) {
        const url = `${window.location.origin}/#/analysis/${this.openedEvent.game_id}?tab=1&event_id=${this.openedEvent.event_id}&analysis_phase=${this.quality_control_analysis_phase}`
        window.open(url, 'event_fixing', 'fullscreen=yes,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no')
      }
    },
    e: function() {
      if(this.user.tiimio_admin) {
        let phase = this.quality_control_analysis_phase
        if(phase == 3 && !this.openedEvent?.run?.run_id) phase = 2

        const url = `${window.location.origin}/#/analysis/${this.openedEvent.game_id}?tab=1&event_id=${this.openedEvent.event_id}&analysis_phase=${phase}`
        window.open(url, 'event_fixing', 'fullscreen=yes,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no')
      }
    },
    f: function() {
      if(this.isSuperAdmin && this.openedEvent && this.is_quality_control) {
        this.unmarkEvent()
      }
    },
    shift_f: function() {
      if(this.isSuperAdmin && this.openedEvent && this.is_quality_control) {
        this.unmarkEvent()
      }
    }
  },
  methods: {
    ...mapActions('filter', [
      'nextEvent',
      'getFilterGames'
    ]),
    ...mapMutations('filter', [
      'SET_CURRENT_FILTER'
    ]),
    ...mapActions('noti', [
      'info',
      'success',
      'error'
    ]),
    ...mapActions('admin', [
      'initSeasons'
    ]),
    async updateFilter(filter) {
      const serializedFilter = JSON.stringify(filter)

      try {
        // Keep URL in sync so refresh / pagination uses the updated filter
        await this.$router.replace({
          query: {
            ...this.$route.query,
            filter: serializedFilter
          }
        }).catch(() => {})

        // Events are already loaded; just swap the active filter so the
        // gameFilteredEvents getter re-applies client-side filtering.
        // Avoid getFilterGames here: the backend overwrites our filter with
        // the saved one when filter_id is present, which would revert changes.
        this.SET_CURRENT_FILTER(filter)

        this.activeTab = 0
        this.info('Suodatus päivitetty')
      } catch (e) {
        console.error('Error updating filter:', e)
        this.error('Virhe suodatuksen päivittämisessä')
      }
    },
    async unmarkEvent() {
      if (!this.openedEvent || !this.openedEvent.event_id) {
        this.error('No event selected')
        return
      }

      this.unmarkingEvent = true

      try {
        // Create a copy of the event with marked set to false
        const updatedEvent = {
          ...this.openedEvent,
          marked: false
        }

        // Send the updated event to the API
        const response = await ax.post('/event', {
          event: updatedEvent,
          updateRunTime: false,
          updatePlayTime: false
        })

        // Update the local state with the response
        this.$store.commit('filter/SET_PLAYING_EVENT', response.data)

        this.success('Eventti merkitty vapaaksi')

        // Move to next event after successful save
        this.nextEvent()
      } catch (error) {
        console.error('Error updating event:', error)
        this.error('Failed to update event')
      } finally {
        this.unmarkingEvent = false
      }
    }
  },
  computed: {
    ...mapGetters('filter', [
      'filter',
      'openedEvent',
      'quality_control_analysis_phase',
      'is_quality_control'
    ]),
    ...mapGetters('user', [
      'currentLeagueName',
      'user',
      'isSuperAdmin',
      'leagueId'
    ]),
    ...mapGetters('admin', [
      'seasons',
      'season_by_id'
    ]),
    showFilterMap() {
      // From the 2026 season onwards the map is also shown for nyp
      if(this.currentLeagueName !== 'nyp') return true
      const season = this.season_by_id(this.filter?.season)
      return newRulesApply(getSeasonYear(season))
    }
  }
}
</script>

<style lang="scss">
.io-filter-bar {
  height: 100%;
}
</style>